import React from 'react'
import RowButton from 'components/atoms/row-button'
import PropTypes from 'prop-types'
import InternalRequestItemStatusBadge from 'domains/internal-request/atoms/item-status-badge'

const actionCellPropTypes = {
  cell: PropTypes.shape({
    row: PropTypes.shape({
      original: PropTypes.object
    })
  })
}

const propTypes = {
  rowButtonProps: PropTypes.arrayOf(PropTypes.object)
}

const defaultProps = {
  rowButtonProps: []
}

const parseSublocationFromLabel = (label) => {
  const bracketMatch = label?.match?.(/\[([^\]]+)\]/)

  return bracketMatch?.[1]
}

const normalizeSubLocationText = (value) => {
  if (value === undefined || value === null || value === '') return null

  const normalizedValue = String(value).trim()

  if (!normalizedValue) return null

  return parseSublocationFromLabel(normalizedValue) || normalizedValue
}

const getSubLocationFromObject = (location) => {
  if (!location) return null

  if (typeof location !== 'object') {
    return normalizeSubLocationText(location)
  }

  return normalizeSubLocationText(
    location.sublocation ||
    location.sub_location ||
    location.sub_location_name ||
    location.box_location_name ||
    location.box_location_label ||
    location.box_sub_location_label ||
    location.data_rak ||
    location.rack ||
    location.rack_name ||
    location.rak ||
    location.location_code ||
    parseSublocationFromLabel(location.label)
  )
}

const getProductBoxLocation = (item, inventory) => {
  const product = item?.product || inventory?.product || {}

  return (
    getSubLocationFromObject(product?.box_location) ||
    getSubLocationFromObject({
      sublocation       : item?.box_location_name || item?.box_location_label || item?.box_sub_location_label ||
        inventory?.box_location_name || inventory?.box_location_label || inventory?.box_sub_location_label ||
        product?.box_location_name || product?.box_location_label || product?.box_sub_location_label ||
        product?.sub_location_label || product?.rack_label || product?.rack_name,
      sub_location      : product?.sub_location,
      sub_location_name : product?.sub_location_name,
      rack              : product?.rack,
      rack_name         : product?.rack_name,
      rak               : product?.rak
    })
  )
}

const getItemSubLocation = (item) => {
  if ([ 'picked', 'borrowed', 'received' ].includes(item?.status)) {
    const dispatchedLocation = getSubLocationFromObject(item?.bnk_warehouse_location)
    if (dispatchedLocation) return dispatchedLocation
  }
  const inventory = item?.inventory || item?.product?.inventory || {}
  const productBoxLocation = getProductBoxLocation(item, inventory)

  if (productBoxLocation) return productBoxLocation

  const explicitItemSubLocation = getSubLocationFromObject({
    sublocation       : item?.borrowed_history_sub_location || item?.sub_location_label || item?.sublocation || inventory?.sub_location_label || inventory?.sublocation,
    sub_location      : item?.sub_location || inventory?.sub_location,
    sub_location_name : item?.sub_location_name || inventory?.sub_location_name,
    data_rak          : item?.data_rak || inventory?.data_rak,
    rack              : item?.rack_label || item?.rack || inventory?.rack_label || inventory?.rack,
    rack_name         : item?.rack_name || inventory?.rack_name,
    rak               : item?.rak || inventory?.rak
  })

  if (explicitItemSubLocation) return explicitItemSubLocation

  const selectedWarehouseLocation = getSubLocationFromObject(
    item?.bnk_warehouse_location || item?.warehouse_location || item?.tl_warehouse_location
  )

  if (selectedWarehouseLocation) return selectedWarehouseLocation

  const locationSources = [
    inventory?.inventory_locations,
    inventory?.locations,
    item?.inventory_locations,
    item?.locations,
    item?.product?.locations
  ]

  for (const locationSource of locationSources) {
    const inventoryLocations = Array.isArray(locationSource) ? locationSource : []

    if (!inventoryLocations.length) continue

    const locationLabels = [
      ...new Set(
        inventoryLocations
          .map((inventoryLocation) => getSubLocationFromObject(inventoryLocation))
          .filter(Boolean)
      )
    ]

    if (locationLabels.length > 0) return locationLabels.join(', ')
  }

  return '-'
}

const LocationCell = (row) => (
  <span className='text-nowrap'>{ getItemSubLocation(row.row.original) }</span>
)

const getProductImageUrl = (rowData = {}) => (
  rowData.inventory?.product?.main_image ||
  rowData.inventory?.product?.main_image_url ||
  rowData.inventory?.product?.product_photo_url ||
  null
)

const ImageCell = (row) => {
  const rowData = row.row.original || {}
  const imageUrl = getProductImageUrl(rowData)

  if (!imageUrl) {
    return (
      <div
        className='d-flex align-items-center justify-content-center rounded bg-light text-muted'
        style={{
          width  : '56px',
          height : '56px'
        }}
      >
        { '-' }
      </div>
    )
  }

  return (
    <div
      className='overflow-hidden rounded bg-light border'
      style={{
        width  : '56px',
        height : '56px'
      }}
    >
      <img
        src={ imageUrl }
        alt={ rowData.inventory?.product?.name || 'Product' }
        loading='lazy'
        decoding='async'
        onError={ (event) => {
          event.currentTarget.style.display = 'none'
        } }
        style={{
          width     : '100%',
          height    : '100%',
          objectFit : 'contain'
        }}
      />
    </div>
  )
}

const generateActionCell = (rowButtonProps) => {
  const buttons = rowButtonProps.map((button) => {
    const ActionCell = ({ cell: { row } }) => {
      const disabled = button.isDisabled ? button.isDisabled(row.original) : false

      return (
        <div className='text-center'>
          <RowButton
            data={ row.original }
            color={ button.colour }
            disabled={ disabled }
            onClick={ button.onClick }
            text={ button.text }
          />
        </div>
      )
    }

    ActionCell.propTypes = actionCellPropTypes

    return {
      Header : button.header,
      id     : button.id,
      Cell   : ActionCell
    }
  })

  return buttons
}

const StatusCell = (row) => <InternalRequestItemStatusBadge status={ row.cell.value } />

/**
 * Columns definition for Seller Product Table
 * @param {array} rowButtonProps - Array props for row button
 */
const InternalRequestItemTableColumns = ({ rowButtonProps, renderLocation }) => (
  [
    {
      Header         : 'Image',
      id             : 'image',
      accessor       : 'inventory.product.main_image',
      Cell           : ImageCell,
      disableFilters : true
    },
    {
      Header         : 'Code',
      id             : 'code',
      accessor       : 'internal_request_code',
      disableFilters : true
    },
    {
      Header   : 'SKU',
      id       : 'inventory_sku',
      accessor : 'inventory.inventory_sku'
    },
    {
      Header   : 'Name',
      id       : 'name',
      accessor : 'inventory.product.name'
    },
    {
      Header         : 'Status',
      id             : 'status',
      accessor       : 'status',
      disableFilters : true,
      Cell           : StatusCell
    },
    {
      Header         : 'Borrowed Qty',
      id             : 'quantity',
      accessor       : 'quantity',
      disableFilters : true
    },
    {
      Header         : <>Quantity<br/>Requested</>,
      id             : 'quantity_requested',
      accessor       : 'quantity',
      disableFilters : true
    },
    {
      Header         : <>Quantity<br/>Picked</>,
      id             : 'quantity_picked',
      accessor       : 'quantity_picked',
      disableFilters : true
    },
    {
      Header         : <>Quantity<br/>Borrowed</>,
      id             : 'borrowed',
      accessor       : 'quantity',
      disableFilters : true
    },
    {
      Header         : <>Quantity Marked<br/>for Return</>,
      id             : 'marked_for_return',
      accessor       : 'quantity_marked_for_return',
      disableFilters : true
    },
    {
      Header         : <>Quantity<br/>Put Away</>,
      id             : 'quantity_put_away',
      accessor       : 'quantity_put_away',
      disableFilters : true
    },
    {
      Header         : <>Quantity<br/>Returned</>,
      id             : 'returned',
      accessor       : 'quantity_returned',
      disableFilters : true
    },
    {
      Header         : <>Quantity<br/>Unreturned</>,
      id             : 'unreturned',
      accessor       : 'quantity_permanently_borrowed',
      disableFilters : true
    },
    {
      Header         : 'Sub-Location',
      id             : 'location',
      accessor       : 'inventory.inventory_locations',
      Cell           : renderLocation ? ({ row }) => renderLocation(row.original) : LocationCell,
      disableFilters : true
    },
    {
      Header         : 'Reason',
      id             : 'reject_reason',
      accessor       : 'reject_reason',
      disableFilters : true
    },
    {
      Header         : 'Reject Note',
      accessor       : 'reject_note',
      disableFilters : true
    },
    {
      Header  : 'Actions',
      columns : [
        ...generateActionCell(rowButtonProps)
      ]
    }
  ]
)

InternalRequestItemTableColumns.propTypes    = propTypes
InternalRequestItemTableColumns.defaultProps = defaultProps

export default InternalRequestItemTableColumns